import React, { useState, useEffect } from 'react';
import { Download, X } from 'lucide-react';
import { useBranding } from '../../contexts/BrandingContext';

/**
 * Detect if the app is already running as an installed PWA.
 * Checks: display-mode standalone/fullscreen (Chrome, Edge, Firefox)
 *         navigator.standalone (iOS Safari)
 */
function isInstalledPWA() {
  if (window.navigator.standalone === true) return true;
  if (window.matchMedia('(display-mode: standalone)').matches) return true;
  if (window.matchMedia('(display-mode: fullscreen)').matches) return true;
  return false;
}

const PWAInstallPrompt = () => {
  const { brandName } = useBranding();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Don't show if already running as installed PWA
    if (isInstalledPWA()) return;
    // Don't show if user previously dismissed
    if (localStorage.getItem('pwa-install-dismissed')) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShow(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Also hide if app becomes installed while banner is visible
    const onInstalled = () => {
      setShow(false);
      setDeferredPrompt(null);
    };
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setShow(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShow(false);
    setDeferredPrompt(null);
    localStorage.setItem('pwa-install-dismissed', 'true');
  };

  if (!show) return null;

  return (
    // Two elements on purpose: the outer one positions (gutter-to-gutter on a
    // phone, centred above 480px), the inner one animates. The stylesheet did both
    // with one transform, so the slide-up keyframe had to carry translateX(-50%)
    // and the mobile breakpoint had to cancel the whole thing.
    //
    // Hidden while an exam is open (ExamRunner marks the body): it covered the
    // paper's navigation bar on a phone, and Install takes the candidate out of
    // the exam window, which the exam records as leaving it.
    <div className="[body[data-exam-active]_&]:tw-hidden [body[data-phone-shell]_&]:tw-bottom-[92px] tw-fixed tw-bottom-3 tw-left-3 tw-right-3 tw-z-toast min-[480px]:tw-bottom-5 min-[480px]:tw-left-1/2 min-[480px]:tw-right-auto min-[480px]:tw-w-max min-[480px]:tw-max-w-[min(560px,calc(100vw-24px))] min-[480px]:tw--translate-x-1/2">
      <div className="tw-flex tw-animate-slide-up tw-items-center tw-gap-2 tw-rounded-xl tw-border tw-border-line tw-bg-surface-raised tw-px-3 tw-py-2.5 tw-shadow-card-hover min-[480px]:tw-gap-3 min-[480px]:tw-px-3.5 min-[480px]:tw-py-3">
        <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-2.5 tw-text-[0.9rem] tw-font-medium tw-text-content [&>svg]:tw-shrink-0">
          <Download size={20} />
          <span className="tw-min-w-0 tw-truncate">Install {brandName || "the app"} for faster access</span>
        </div>
        <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-2">
          <button
            type="button"
            className="tw-shrink-0 tw-whitespace-nowrap tw-rounded-lg tw-border-0 tw-bg-primary-solid tw-px-4 tw-py-1.5 tw-text-[0.85rem] tw-font-semibold tw-text-primary-solid-fg tw-transition-colors hover:tw-bg-primary-solid/90"
            onClick={handleInstall}
          >
            Install
          </button>
          <button
            type="button"
            className="tw-flex tw-h-8 tw-w-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-md tw-border-0 tw-bg-transparent tw-p-0 tw-text-content-muted tw-transition-colors hover:tw-bg-surface-hover hover:tw-text-content"
            onClick={handleDismiss}
            aria-label="Dismiss"
          >
            <X size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
